// What each night's stay includes (breakfast, dinner, campfire...), worked out the same way the
// customer's Trip Details page does on the server (TripTrackingService.includedFacilityLabels and
// mergedIncludedFacilityLabels): anything the saved stay says is included, plus everything the
// hotel itself includes that is not a paid optional extra. Keep the two in step.

const FLAG_NAMES = [
  ["breakfast", "Breakfast"],
  ["dinner", "Dinner"],
  ["campfire", "Campfire"],
  ["swimmingPool", "Swimming Pool"],
];

/** Night n (n > 0) is stayed on travelDate + n - 1; pre-night -i on travelDate - i. */
function stayDate(travelDate, night) {
  const date = /^\d{4}-\d{2}-\d{2}/.test(String(travelDate || "")) ? String(travelDate).slice(0, 10) : "";
  if (!date) return "";
  return addDays(date, night < 0 ? night : night - 1);
}

function inclusionNames(stay, hotel) {
  const names = [];
  const add = (name) => {
    const clean = String(name || "").trim();
    if (clean && !names.some((existing) => existing.toLowerCase() === clean.toLowerCase())) names.push(clean);
  };
  FLAG_NAMES.forEach(([flag, name]) => { if (stay?.[flag] === true) add(name); });
  // On the saved stay the four flags above are the word on those four; its facility list only
  // adds the rest.
  (stay?.facilities || [])
    .filter((facility) => facility?.available === true && !isFlagName(facility.name))
    .forEach((facility) => add(facility.name));
  FLAG_NAMES.forEach(([flag, name]) => { if (hotel?.[flag] === true) add(name); });
  // A hotel's list carries every facility, with available saying which this hotel has.
  (hotel?.facilities || [])
    .filter((facility) => facility?.available === true && facility?.active !== false && !isPaidOptional(facility))
    .forEach((facility) => add(facility.name));
  return names;
}

function isFlagName(name) {
  const clean = String(name || "").trim().toLowerCase();
  return FLAG_NAMES.some(([, flagName]) => flagName.toLowerCase() === clean);
}

function isPaidOptional(facility) {
  return facility?.costBehavior === "OPTIONAL_PAID" || facility?.optionalWithCost === true;
}

/**
 * How customer documents name a night: "Day 2 (Check-in 27/10/2026)". `night` is the saved night
 * number (1, 2 ... package and post-nights; -1, -2 ... pre-nights). Days count from the first night
 * away, so with `preNights` extra pre-nights a pre-night is Day 1 and the package's own Day 1 moves
 * down. A no-stay night shows its date without "Check-in".
 */
export function tripDayLabel(night, travelDate, preNights = 0, noStay = false) {
  const day = (night < 0 ? night : night - 1) + Math.max(0, Number(preNights) || 0) + 1;
  const date = stayDate(travelDate, night);
  return `Day ${day}${date ? ` (${noStay ? "" : "Check-in "}${formatDate(date)})` : ""}`;
}

/**
 * One line per night, e.g. "Day 2 (Check-in 24/10/2026): Breakfast on checkout morning (25/10/2026),
 * Dinner, Campfire". `hotelById` looks a stay's hotel up by id; nights without a hotel are skipped.
 * Days count from `tripStartDate` (the first extra pre-night, when there is one), the way the
 * receipt's room lines do, so a pre-night is Day 1 and the package's own Day 1 moves down.
 */
export function stayInclusionLines(stays, hotelById, travelDate, tripStartDate = travelDate) {
  const byNight = new Map();
  (stays || []).forEach((stay) => {
    const saved = Number(stay?.nightNumber || stay?.nightIndex || 0);
    const night = saved === 0 ? 1 : saved;
    const hotel = hotelById(stay?.hotelId);
    const names = byNight.get(night) || [];
    inclusionNames(stay, hotel).forEach((name) => {
      if (!names.some((existing) => existing.toLowerCase() === name.toLowerCase())) names.push(name);
    });
    byNight.set(night, names);
  });
  const preNights = Math.max(
    daysBetween(isoDate(tripStartDate), isoDate(travelDate)),
    ...Array.from(byNight.keys()).map((night) => -night),
    0
  );
  return Array.from(byNight.entries())
    .sort(([left], [right]) => left - right)
    .map(([night, names]) => {
      const date = stayDate(travelDate, night);
      const label = tripDayLabel(night, travelDate, preNights);
      const items = names.map((name) => {
        if (name.toLowerCase() !== "breakfast") return name;
        return date ? `Breakfast on checkout morning (${formatDate(addDays(date, 1))})` : "Breakfast on checkout morning";
      });
      return `${label}: ${items.length ? items.join(", ") : "No meals included"}`;
    });
}

function addDays(isoDate, days) {
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

function isoDate(value) {
  return /^\d{4}-\d{2}-\d{2}/.test(String(value || "")) ? String(value).slice(0, 10) : "";
}

/** Whole days from `from` to `to`; 0 when either is missing. */
function daysBetween(from, to) {
  if (!from || !to) return 0;
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000);
}

function formatDate(isoDate) {
  const [year, month, day] = isoDate.split("-");
  return `${day}/${month}/${year}`;
}
